import { useEffect, useState, useCallback } from 'react';
import { MapPin, Edit2, Save, X, Search, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CountriesManagement = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCountry, setEditingCountry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [editData, setEditData] = useState({
    name_fa: '',
    name_en: '',
    capital_fa: '',
    flag_emoji: '',
    population: '',
    official_language: '',
    currency: '',
    climate: '',
    avg_living_cost: '',
    avg_rent: '',
    avg_salary: '',
    job_market: '',
    security_score: 0,
    quality_of_life_score: 0,
    education_score: 0,
    healthcare_score: 0,
    transportation: '',
    residence_conditions: '',
    citizenship_conditions: '',
    best_cities: [],
    best_student_cities: [],
    top_fields: [],
    in_demand_jobs: [],
    pros: [],
    cons: [],
    image_url: '',
    major_cities: [],
    popular_cities: []
  });

  const fetchCountries = useCallback(async () => {
    try {
      const response = await api.get('/countries');
      setCountries(response.data);
    } catch (error) {
      toast.error('خطا در بارگذاری کشورها');
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps is intentional - only fetch on mount

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const startEdit = (country) => {
    setEditingCountry(country.id);
    setEditData({
      name_fa: country.name_fa || '',
      name_en: country.name_en || '',
      capital_fa: country.capital_fa || '',
      flag_emoji: country.flag_emoji || '',
      population: country.population || '',
      official_language: country.official_language || '',
      currency: country.currency || '',
      climate: country.climate || '',
      avg_living_cost: country.avg_living_cost || '',
      avg_rent: country.avg_rent || '',
      avg_salary: country.avg_salary || '',
      job_market: country.job_market || '',
      security_score: country.security_score || 0,
      quality_of_life_score: country.quality_of_life_score || 0,
      education_score: country.education_score || 0,
      healthcare_score: country.healthcare_score || 0,
      transportation: country.transportation || '',
      residence_conditions: country.residence_conditions || '',
      citizenship_conditions: country.citizenship_conditions || '',
      best_cities: country.best_cities || [],
      best_student_cities: country.best_student_cities || [],
      top_fields: country.top_fields || [],
      in_demand_jobs: country.in_demand_jobs || [],
      pros: country.pros || [],
      cons: country.cons || [],
      image_url: country.image_url || '',
      major_cities: country.major_cities || [],
      popular_cities: country.popular_cities || []
    });
  };

  const cancelEdit = () => {
    setEditingCountry(null);
  };

  const handleSave = async (countryId) => {
    try {
      // Ensure scores are valid numbers and include id
      const payload = {
        ...editData,
        id: countryId,
        security_score: parseFloat(editData.security_score) || 0,
        quality_of_life_score: parseFloat(editData.quality_of_life_score) || 0,
        education_score: parseFloat(editData.education_score) || 0,
        healthcare_score: parseFloat(editData.healthcare_score) || 0,
      };
      
      await api.put(`/countries/${countryId}`, payload);
      toast.success('اطلاعات کشور با موفقیت به‌روز شد');
      fetchCountries();
      cancelEdit();
    } catch (error) {
      toast.error('خطا در به‌روزرسانی کشور');
    }
  };

  const handleArrayItemChange = (arrayName, index, value) => {
    const newArray = [...editData[arrayName]];
    newArray[index] = value;
    setEditData({ ...editData, [arrayName]: newArray });
  };

  const addArrayItem = (arrayName) => {
    setEditData({
      ...editData,
      [arrayName]: [...editData[arrayName], '']
    });
  };

  const removeArrayItem = (arrayName, index) => {
    const newArray = editData[arrayName].filter((_, i) => i !== index);
    setEditData({ ...editData, [arrayName]: newArray });
  };

  const filteredCountries = countries.filter(country =>
    country.name_fa?.includes(searchTerm) || 
    country.name_en?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">در حال بارگذاری...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-light text-white mb-2">مدیریت کامل کشورها</h1>
            <p className="text-zinc-400">ویرایش همه اطلاعات کشورها شامل امنیت، قیمت‌ها و...</p>
          </div>
          <MapPin className="text-[#D4AF37]" size={40} />
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
            <input
              type="text"
              placeholder="جستجوی کشور..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg pr-10 pl-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
        </div>

        {/* Countries List */}
        <div className="space-y-4">
          {filteredCountries.map((country) => (
            <div
              key={country.id}
              className="glass-effect border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
            >
              {editingCountry === country.id ? (
                // Edit Mode - Complete Form
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-medium text-white">{country.flag_emoji} {country.name_fa}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave(country.id)}
                        className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#F4D03F] transition-colors flex items-center gap-2"
                      >
                        <Save size={18} /> ذخیره
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors flex items-center gap-2"
                      >
                        <X size={18} /> لغو
                      </button>
                    </div>
                  </div>

                  {/* Basic Info */}
                  <div className="border-b border-white/10 pb-4">
                    <h4 className="text-lg text-[#D4AF37] mb-3">اطلاعات پایه</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">نام فارسی *</label>
                        <input
                          type="text"
                          value={editData.name_fa}
                          onChange={(e) => setEditData({ ...editData, name_fa: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">نام انگلیسی *</label>
                        <input
                          type="text"
                          value={editData.name_en}
                          onChange={(e) => setEditData({ ...editData, name_en: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">پایتخت</label>
                        <input
                          type="text"
                          value={editData.capital_fa}
                          onChange={(e) => setEditData({ ...editData, capital_fa: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">پرچم (Emoji)</label>
                        <input
                          type="text"
                          value={editData.flag_emoji}
                          onChange={(e) => setEditData({ ...editData, flag_emoji: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none text-2xl"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">جمعیت</label>
                        <input
                          type="text"
                          placeholder="38 میلیون"
                          value={editData.population}
                          onChange={(e) => setEditData({ ...editData, population: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">زبان رسمی</label>
                        <input
                          type="text"
                          value={editData.official_language}
                          onChange={(e) => setEditData({ ...editData, official_language: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">واحد پول</label>
                        <input
                          type="text"
                          value={editData.currency}
                          onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">آب و هوا</label>
                        <input
                          type="text"
                          value={editData.climate}
                          onChange={(e) => setEditData({ ...editData, climate: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">URL تصویر</label>
                        <input
                          type="text"
                          value={editData.image_url}
                          onChange={(e) => setEditData({ ...editData, image_url: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Economic Info */}
                  <div className="border-b border-white/10 pb-4">
                    <h4 className="text-lg text-[#D4AF37] mb-3">اطلاعات اقتصادی</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">💰 هزینه زندگی ماهانه (USD)</label>
                        <input
                          type="text"
                          placeholder="$2,400-$2,800"
                          value={editData.avg_living_cost}
                          onChange={(e) => setEditData({ ...editData, avg_living_cost: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">🏠 اجاره متوسط</label>
                        <input
                          type="text"
                          value={editData.avg_rent}
                          onChange={(e) => setEditData({ ...editData, avg_rent: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">💵 حقوق متوسط</label>
                        <input
                          type="text"
                          value={editData.avg_salary}
                          onChange={(e) => setEditData({ ...editData, avg_salary: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">بازار کار</label>
                        <input
                          type="text"
                          value={editData.job_market}
                          onChange={(e) => setEditData({ ...editData, job_market: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">سیستم حمل‌ونقل</label>
                        <input
                          type="text"
                          value={editData.transportation}
                          onChange={(e) => setEditData({ ...editData, transportation: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Scores */}
                  <div className="border-b border-white/10 pb-4">
                    <h4 className="text-lg text-[#D4AF37] mb-3">امتیازها (0-10)</h4>
                    <div className="grid md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">🛡️ امنیت</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={editData.security_score}
                          onChange={(e) => setEditData({ ...editData, security_score: parseFloat(e.target.value) })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">⭐ کیفیت زندگی</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={editData.quality_of_life_score}
                          onChange={(e) => setEditData({ ...editData, quality_of_life_score: parseFloat(e.target.value) })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">📚 آموزش</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={editData.education_score}
                          onChange={(e) => setEditData({ ...editData, education_score: parseFloat(e.target.value) })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">🏥 سلامت</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="10"
                          value={editData.healthcare_score}
                          onChange={(e) => setEditData({ ...editData, healthcare_score: parseFloat(e.target.value) })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Immigration Info */}
                  <div className="border-b border-white/10 pb-4">
                    <h4 className="text-lg text-[#D4AF37] mb-3">اطلاعات مهاجرت</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">شرایط اقامت</label>
                        <textarea
                          rows="3"
                          value={editData.residence_conditions}
                          onChange={(e) => setEditData({ ...editData, residence_conditions: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-zinc-400 mb-2">شرایط شهروندی</label>
                        <textarea
                          rows="3"
                          value={editData.citizenship_conditions}
                          onChange={(e) => setEditData({ ...editData, citizenship_conditions: e.target.value })}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Arrays Section */}
                  <div className="space-y-4">
                    {/* Best Cities */}
                    <ArrayEditor
                      label="🏙️ بهترین شهرها"
                      arrayName="best_cities"
                      items={editData.best_cities}
                      onChange={handleArrayItemChange}
                      onAdd={addArrayItem}
                      onRemove={removeArrayItem}
                      countryId={country.id}
                    />

                    {/* Best Student Cities */}
                    <ArrayEditor
                      label="🎓 بهترین شهرهای دانشجویی"
                      arrayName="best_student_cities"
                      items={editData.best_student_cities}
                      onChange={handleArrayItemChange}
                      onAdd={addArrayItem}
                      onRemove={removeArrayItem}
                      countryId={country.id}
                    />

                    {/* Top Fields */}
                    <ArrayEditor
                      label="📚 رشته‌های برتر"
                      arrayName="top_fields"
                      items={editData.top_fields}
                      onChange={handleArrayItemChange}
                      onAdd={addArrayItem}
                      onRemove={removeArrayItem}
                      countryId={country.id}
                    />

                    {/* In Demand Jobs */}
                    <ArrayEditor
                      label="💼 مشاغل پرتقاضا"
                      arrayName="in_demand_jobs"
                      items={editData.in_demand_jobs}
                      onChange={handleArrayItemChange}
                      onAdd={addArrayItem}
                      onRemove={removeArrayItem}
                      countryId={country.id}
                    />

                    {/* Pros */}
                    <ArrayEditor
                      label="✅ مزایا"
                      arrayName="pros"
                      items={editData.pros}
                      onChange={handleArrayItemChange}
                      onAdd={addArrayItem}
                      onRemove={removeArrayItem}
                      countryId={country.id}
                    />

                    {/* Cons */}
                    <ArrayEditor
                      label="❌ معایب"
                      arrayName="cons"
                      items={editData.cons}
                      onChange={handleArrayItemChange}
                      onAdd={addArrayItem}
                      onRemove={removeArrayItem}
                      countryId={country.id}
                    />
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-2xl font-medium text-white mb-3">
                        {country.flag_emoji} {country.name_fa} ({country.name_en})
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-zinc-400 mb-1">
                            <span className="text-[#D4AF37]">🏛️ پایتخت:</span> {country.capital_fa}
                          </p>
                          <p className="text-zinc-400 mb-1">
                            <span className="text-[#D4AF37]">💰 هزینه زندگی:</span> {country.avg_living_cost}
                          </p>
                          <p className="text-zinc-400 mb-1">
                            <span className="text-[#D4AF37]">🏠 اجاره:</span> {country.avg_rent}
                          </p>
                          <p className="text-zinc-400 mb-1">
                            <span className="text-[#D4AF37]">👥 جمعیت:</span> {country.population}
                          </p>
                        </div>
                        <div>
                          <p className="text-zinc-400 mb-1">
                            <span className="text-[#D4AF37]">🛡️ امنیت:</span> {country.security_score}/10
                          </p>
                          <p className="text-zinc-400 mb-1">
                            <span className="text-[#D4AF37]">⭐ کیفیت زندگی:</span> {country.quality_of_life_score}/10
                          </p>
                          <p className="text-zinc-400 mb-1">
                            <span className="text-[#D4AF37]">📚 آموزش:</span> {country.education_score}/10
                          </p>
                          <p className="text-zinc-400 mb-1">
                            <span className="text-[#D4AF37]">🏥 سلامت:</span> {country.healthcare_score}/10
                          </p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => startEdit(country)}
                      className="p-3 bg-white/5 hover:bg-[#D4AF37]/20 text-[#D4AF37] rounded-lg transition-colors"
                    >
                      <Edit2 size={20} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredCountries.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-400">هیچ کشوری یافت نشد</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Array Editor Component
const ArrayEditor = ({ label, arrayName, items, onChange, onAdd, onRemove, countryId }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm text-zinc-400">{label}</label>
      <button
        onClick={() => onAdd(arrayName)}
        className="text-xs px-3 py-1 bg-[#D4AF37]/20 text-[#D4AF37] rounded hover:bg-[#D4AF37]/30 flex items-center gap-1"
      >
        <Plus size={14} /> افزودن
      </button>
    </div>
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={`${arrayName}-${countryId}-${index}`} className="flex gap-2">
          <input
            type="text"
            value={item}
            onChange={(e) => onChange(arrayName, index, e.target.value)}
            placeholder={`آیتم ${index + 1}...`}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:border-[#D4AF37] focus:outline-none"
          />
          <button
            onClick={() => onRemove(arrayName, index)}
            className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>
  </div>
);

export default CountriesManagement;
